import { useEffect, useState } from 'react'
import { Dialog, Button, Input } from './ui'
import type { PaymentHistoryItem } from '../utils/api'

/**
 * 領収書発行ダイアログ（PaymentHistory の各支払い行から起動）。
 *
 * 発行者は非課税の個人（屋号「干し草屋」）を前提とし、インボイス登録番号・消費税の
 * 内訳は記載しない通常の領収書とする。宛名・但し書きは編集可能。金額・発行日・
 * 領収書番号は対象の支払い記録から確定させる。印刷は別ウィンドウに描画して
 * ブラウザの印刷（PDF保存）に委ねる。
 */

// 発行者情報。屋号のみ＋公開済みの問い合わせ先（信頼性のため）。住所・氏名は記載しない。
const ISSUER_NAME = '干し草屋'
const ISSUER_CONTACT = 'support@hoshikusaya.com'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP')
}

/** 支払い日時からユーザー内で一意な領収書番号を生成する（再発行でも不変）。 */
function receiptNumber(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `otomochi-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
}

/** 種別に応じた但し書きの初期値。 */
function defaultNote(item: PaymentHistoryItem): string {
  if (item.payment_type === 'oneshot') {
    return (item.sessions ?? 0) > 0 ? 'otomochi 追加回数 利用料として' : 'otomochi 追加時間 利用料として'
  }
  if (item.payment_type === 'subscription') return 'otomochi 月額利用料として'
  if (item.payment_type === 'plan_change') return 'otomochi プラン変更 差額利用料として'
  return 'otomochi 利用料として'
}

/** HTML 特殊文字をエスケープする（印刷ドキュメント埋め込み用）。 */
function escapeHtml(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

interface ReceiptDialogProps {
  open: boolean
  onClose: () => void
  /** 発行対象の支払い記録。null のときは何も描画しない。 */
  item: PaymentHistoryItem | null
  /** 宛名の初期値（アカウントの表示名やメール）。 */
  defaultRecipient?: string
}

export default function ReceiptDialog({ open, onClose, item, defaultRecipient }: ReceiptDialogProps) {
  const [recipient, setRecipient] = useState('')
  const [note, setNote] = useState('')

  // 対象明細が変わるたびに初期値を再設定する。
  useEffect(() => {
    if (item) {
      setRecipient(defaultRecipient ?? '')
      setNote(defaultNote(item))
    }
  }, [item, defaultRecipient])

  if (!item) return null

  const amount = item.amount ?? 0
  const number = receiptNumber(item.created_at)
  const issueDate = formatDate(item.created_at)
  const recipientLabel = recipient.trim() || '上様'
  const noteLabel = note.trim() || 'otomochi 利用料として'

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=760,height=680')
    if (!win) return // ポップアップブロック時は無操作

    win.document.write(`<!doctype html>
<html lang="ja"><head><meta charset="utf-8" />
<title>領収書 ${escapeHtml(number)}</title>
<style>
  body { font-family: sans-serif; color: #1f2937; margin: 40px; }
  .box { max-width: 620px; margin: 0 auto; border: 1px solid #e5e7eb; padding: 32px; }
  h1 { font-size: 26px; letter-spacing: 0.3em; margin: 0 0 24px; text-align: center;
       border-bottom: 2px solid #1f2937; padding-bottom: 8px; }
  .meta { text-align: right; font-size: 12px; color: #6b7280; line-height: 1.6; }
  .to { font-size: 18px; margin: 8px 0 20px; border-bottom: 1px solid #9ca3af; padding-bottom: 4px; display: inline-block; min-width: 260px; }
  .amount { font-size: 30px; font-weight: bold; text-align: center;
            border-top: 1px solid #1f2937; border-bottom: 1px solid #1f2937; padding: 14px 0; margin: 12px 0 20px; }
  .note { font-size: 14px; margin-bottom: 28px; }
  .stmt { font-size: 13px; color: #374151; margin-bottom: 28px; }
  .issuer { text-align: right; font-size: 13px; line-height: 1.7; }
  .issuer .name { font-size: 16px; font-weight: bold; }
  @media print { body { margin: 0; } .box { border: none; } }
</style></head>
<body>
  <div class="box">
    <h1>領収書</h1>
    <div class="meta">No. ${escapeHtml(number)}<br />発行日: ${escapeHtml(issueDate)}</div>
    <div><span class="to">${escapeHtml(recipientLabel)} 様</span></div>
    <div class="amount">¥ ${amount.toLocaleString()} -</div>
    <div class="note">但し ${escapeHtml(noteLabel)}</div>
    <div class="stmt">上記正に領収いたしました。</div>
    <div class="issuer"><div class="name">${escapeHtml(ISSUER_NAME)}</div>${escapeHtml(ISSUER_CONTACT)}</div>
  </div>
</body></html>`)
    win.document.close()
    win.focus()
    win.print()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="領収書の発行"
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            閉じる
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            印刷 / PDF保存
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <label className="block">
          <span className="block mb-1 font-medium text-text-heading">宛名</span>
          <Input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="上様"
          />
        </label>

        <label className="block">
          <span className="block mb-1 font-medium text-text-heading">但し書き</span>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        <div className="rounded-field bg-background p-4 space-y-1 text-text-sub">
          <div className="flex justify-between">
            <span>金額</span>
            <span className="font-bold text-text-heading">¥{amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>発行日</span>
            <span>{issueDate}</span>
          </div>
          <div className="flex justify-between">
            <span>領収書番号</span>
            <span>{number}</span>
          </div>
          <div className="flex justify-between">
            <span>発行者</span>
            <span>{ISSUER_NAME}</span>
          </div>
        </div>

        <p className="text-xs text-text-sub">
          「印刷 / PDF保存」を押すと領収書が別ウィンドウで開きます。印刷ダイアログの保存先で
          「PDF」を選ぶとPDFとして保存できます。
        </p>
      </div>
    </Dialog>
  )
}
